import React, { useEffect, useRef, useState } from 'react';
import { Terminal as XTerminal } from 'xterm';
import { FitAddon } from '@xterm/addon-fit';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { X, Terminal, ChevronRight } from 'lucide-react';
import 'xterm/css/xterm.css';

const CISCO_RESPONSES = {
  'show version': (device) => `
Cisco IOS Software, Version 15.7(3)M2, RELEASE SOFTWARE
Technical Support: http://www.cisco.com/techsupport
Copyright (c) 1986-2018 by Cisco Systems, Inc.

ROM: Bootstrap program is C2900 Software
${device.hostname} uptime is 47 days, 3 hours, 22 minutes
System image file is "flash:c2900-universalk9-mz.SPA.157-3.M2.bin"

cisco CISCO2911/K9 (revision 1.0) with 491520K/32768K bytes of memory.
Processor board ID FTX1641AHK7
4 Gigabit Ethernet interfaces
1 terminal line
Configuration register is 0x2102
`,

  'show ip interface brief': (device) => {
    const ip = (device.ip_addresses || [])[0] || '192.168.1.1';
    return `
Interface              IP-Address      OK? Method Status                Protocol
GigabitEthernet0/0     ${ip}   YES NVRAM  up                    up
GigabitEthernet0/1     unassigned      YES NVRAM  administratively down down
GigabitEthernet0/2     10.0.0.1        YES NVRAM  up                    up
GigabitEthernet0/3     unassigned      YES NVRAM  administratively down down
Loopback0              127.0.0.1       YES NVRAM  up                    up
`;
  },

  'show arp': (device) => `
Protocol  Address          Age (min)  Hardware Addr   Type   Interface
Internet  192.168.1.1             -   ${(device.mac_addresses || [])[0] || 'a0b1.c2d3.e4f5'}   ARPA   GigabitEthernet0/0
Internet  192.168.1.10           12   0a1b.2c3d.4e5f  ARPA   GigabitEthernet0/0
Internet  192.168.1.20            5   1122.3344.5566  ARPA   GigabitEthernet0/0
Internet  10.0.0.2               32   aabb.ccdd.eeff  ARPA   GigabitEthernet0/2
Internet  10.0.0.3                1   1234.5678.9abc  ARPA   GigabitEthernet0/2
`,

  'show running-config': (device) => `
Building configuration...

Current configuration : 3124 bytes
!
version 15.7
service timestamps debug datetime msec
service timestamps log datetime msec
service password-encryption
!
hostname ${device.hostname || 'Router'}
!
boot-start-marker
boot-end-marker
!
!
aaa new-model
!
ip audit notify log
ip audit po max-events 100
!
interface GigabitEthernet0/0
 description WAN Interface
 ip address ${(device.ip_addresses || [])[0] || '192.168.1.1'} 255.255.255.0
 ip access-group BLOCK_MALICIOUS in
 duplex auto
 speed auto
!
interface GigabitEthernet0/2
 description LAN Interface
 ip address 10.0.0.1 255.255.255.0
 duplex auto
 speed auto
!
ip route 0.0.0.0 0.0.0.0 192.168.1.254
!
access-list 1 permit 192.168.1.0 0.0.0.255
access-list 100 deny   ip any 10.0.0.0 0.255.255.255 log
access-list 100 permit ip any any
!
line con 0
line aux 0
line vty 0 4
 access-class 1 in
 login local
 transport input ssh
!
end
`,

  'show interfaces': (device) => `
GigabitEthernet0/0 is up, line protocol is up
  Hardware is CN Gigabit Ethernet, address is ${(device.mac_addresses || [])[0] || 'a0b1.c2d3.e4f5'} (bia a0b1.c2d3.e4f5)
  Description: WAN Interface
  Internet address is ${(device.ip_addresses || [])[0] || '192.168.1.1'}/24
  MTU 1500 bytes, BW 1000000 Kbit/sec, DLY 10 usec,
     reliability 255/255, txload 1/255, rxload 1/255
  Encapsulation ARPA, loopback not set
  Keepalive set (10 sec)
  Full Duplex, 1Gbps, media type is RJ45
  5 minute input rate 8540000 bits/sec, 1284 packets/sec
  5 minute output rate 6120000 bits/sec, 921 packets/sec
     1287493028 packets input, 1023840283 bytes, 0 no buffer
     0 input errors, 0 CRC, 0 frame
     987234921 packets output, 921034812 bytes, 0 underruns
`,

  'show ip route': () => `
Codes: L - local, C - connected, S - static, R - RIP, M - mobile, B - BGP
       D - EIGRP, EX - EIGRP external, O - OSPF, IA - OSPF inter area

Gateway of last resort is 192.168.1.254 to network 0.0.0.0

S*    0.0.0.0/0 [1/0] via 192.168.1.254
      10.0.0.0/8 is variably subnetted, 2 subnets, 2 masks
C        10.0.0.0/24 is directly connected, GigabitEthernet0/2
L        10.0.0.1/32 is directly connected, GigabitEthernet0/2
      192.168.1.0/24 is variably subnetted, 2 subnets, 2 masks
C        192.168.1.0/24 is directly connected, GigabitEthernet0/0
L        192.168.1.1/32 is directly connected, GigabitEthernet0/0
`,

  'show cdp neighbors': () => `
Capability Codes: R - Router, T - Trans Bridge, B - Source Route Bridge
                  S - Switch, H - Host, I - IGMP, r - Repeater, P - Phone,
                  D - Remote, C - CVTA, M - Two-port Mac Relay

Device ID        Local Intrfce     Holdtme    Capability  Platform  Port ID
Switch-Core      Gig 0/0            170              S I   WS-C2960  Gig 0/1
Router-Edge      Gig 0/2            153             R S I  CISCO2911 Gig 0/0
`,

  'show ip bgp summary': () => `
BGP router identifier 192.168.1.1, local AS number 65001
BGP table version is 142, main routing table version 142

Neighbor        V           AS MsgRcvd MsgSent   TblVer  InQ OutQ Up/Down  State/PfxRcd
10.0.0.2        4        65002   87234   65123      142    0    0 3d02h         1
`,

  'ping': (device, args) => {
    const target = args[1] || '8.8.8.8';
    return `
Type escape sequence to abort.
Sending 5, 100-byte ICMP Echos to ${target}, timeout is 2 seconds:
!!!!!
Success rate is 100 percent (5/5), round-trip min/avg/max = 1/2/4 ms
`;
  },

  'traceroute': (device, args) => {
    const target = args[1] || '8.8.8.8';
    return `
Type escape sequence to abort.
Tracing the route to ${target}
VRF info: (vrf in name/id, vrf out name/id)
  1 192.168.1.254 1 msec 1 msec 1 msec
  2 10.10.1.1 3 msec 2 msec 3 msec
  3 172.16.0.1 8 msec 9 msec 8 msec
  4 ${target} 12 msec 11 msec 13 msec
`;
  },

  'show ip access-lists': () => `
Extended IP access list BLOCK_MALICIOUS
    10 deny ip 185.220.0.0 0.0.255.255 any log (1243 matches)
    20 deny ip 45.95.168.0 0.0.0.255 any log (87 matches)
    30 deny ip 91.108.4.0 0.0.3.255 any log (23 matches)
    40 permit ip any any (15234892 matches)
`,

  'show log': () => {
    const now = new Date();
    return `
Syslog logging: enabled (0 messages dropped, 3 messages rate-limited,
                0 flushes, 0 overruns, xml disabled, filtering disabled)
    Console logging: level debugging, 234 messages logged, xml disabled,
                     filtering disabled
    Monitor logging: level debugging, 0 messages logged, xml disabled,
                     filtering disabled
    Buffer logging:  level debugging, 234 messages logged, xml disabled,
                    filtering disabled

Log Buffer (4096 bytes):
${now.toISOString().substring(0,19)}: %SEC-6-IPACCESSLOGP: list BLOCK_MALICIOUS denied tcp 185.220.101.47(34521) -> 192.168.1.1(22), 1 packet
${now.toISOString().substring(0,19)}: %LINK-3-UPDOWN: Interface GigabitEthernet0/0, changed state to up
${now.toISOString().substring(0,19)}: %SEC-6-IPACCESSLOGP: list BLOCK_MALICIOUS denied tcp 45.95.168.132(45123) -> 192.168.1.1(80), 1 packet
${now.toISOString().substring(0,19)}: %SYS-5-CONFIG_I: Configured from console by admin on vty0 (192.168.1.10)
`;
  },

  'enable': () => '\nPassword: \n',
  'configure terminal': () => '\nEnter configuration commands, one per line.  End with CNTL/Z.\n',
  'exit': () => '\n',
  'logout': () => '\n[Connection to device closed by remote host]\n',
  '?': () => `
Exec commands:
  clear     Reset functions
  configure  Enter configuration mode
  debug      Debugging functions
  disable    Turn off privileged commands
  enable     Turn on privileged commands
  exit       Exit from the EXEC
  logout     Exit from the EXEC
  ping       Send echo messages
  reload     Halt and perform a cold restart
  show       Show running system information
  ssh        Open a secure shell client connection
  telnet     Open a telnet connection
  terminal   Set terminal line parameters
  traceroute Trace route to destination
  undebug    Disable debugging functions
  write      Write running configuration to memory, network, or terminal
`,
};

export function CiscoTerminal({ device, onClose }) {
  const termRef = useRef(null);
  const xtermRef = useRef(null);
  const fitAddonRef = useRef(null);
  const [currentLine, setCurrentLine] = useState('');
  const promptRef = useRef('');
  const hostname = device.hostname || 'Router';

  useEffect(() => {
    if (!termRef.current) return;

    const term = new XTerminal({
      theme: {
        background: '#0a0a0a',
        foreground: '#00ff41',
        cursor: '#00ff41',
        selection: '#1a3a1a',
        green: '#00ff41',
        brightGreen: '#39ff14',
      },
      fontFamily: '"Courier New", Courier, monospace',
      fontSize: 13,
      cursorBlink: true,
      convertEol: true,
    });

    const fitAddon = new FitAddon();
    term.loadAddon(fitAddon);
    term.open(termRef.current);
    
    // Delay fit to ensure DOM has proper dimensions
    const doFit = () => {
      try {
        if (termRef.current && termRef.current.offsetWidth > 0) {
          fitAddon.fit();
        }
      } catch (e) {
        // Ignore dimensions errors during initial render
      }
    };
    setTimeout(doFit, 100);
    setTimeout(doFit, 300);

    xtermRef.current = term;
    fitAddonRef.current = fitAddon;

    const prompt = `${hostname}#`;
    promptRef.current = prompt;

    // Welcome banner
    term.writeln('\x1b[32m');
    term.writeln('╔══════════════════════════════════════════════════════════╗');
    term.writeln('║         INFRASHIELD — CISCO IOS TERMINAL EMULATOR        ║');
    term.writeln('╚══════════════════════════════════════════════════════════╝\x1b[0m');
    term.writeln(`\x1b[33mDevice: ${device.hostname} | Type: ${device.type?.toUpperCase()} | IP: ${(device.ip_addresses || [])[0] || 'N/A'}\x1b[0m`);
    term.writeln('\x1b[36mDigite "?" para ver os comandos disponíveis\x1b[0m');
    term.writeln('');
    term.write(`\x1b[32m${prompt} \x1b[0m`);

    let lineBuffer = '';

    term.onKey(({ key, domEvent }) => {
      const printable = !domEvent.altKey && !domEvent.ctrlKey && !domEvent.metaKey;

      if (domEvent.key === 'Enter') {
        term.writeln('');
        const cmd = lineBuffer.trim().toLowerCase();
        const parts = cmd.split(/\s+/);
        const baseCmd = parts[0];

        if (cmd) {
          const handler = CISCO_RESPONSES[cmd] || CISCO_RESPONSES[baseCmd];
          if (handler) {
            const output = handler(device, parts);
            term.writeln(`\x1b[37m${output}\x1b[0m`);
          } else if (cmd === 'clear' || cmd === 'cls') {
            term.clear();
          } else {
            term.writeln(`\x1b[31m% Unrecognized command: "${cmd}"\x1b[0m`);
            term.writeln('\x1b[33mDigite "?" para ver os comandos disponíveis\x1b[0m');
            term.writeln('');
          }
        }

        lineBuffer = '';
        term.write(`\x1b[32m${prompt} \x1b[0m`);
      } else if (domEvent.key === 'Backspace') {
        if (lineBuffer.length > 0) {
          lineBuffer = lineBuffer.slice(0, -1);
          term.write('\b \b');
        }
      } else if (domEvent.key === 'Tab') {
        // Tab completion
        const completions = Object.keys(CISCO_RESPONSES).filter(k => k.startsWith(lineBuffer));
        if (completions.length === 1) {
          const completion = completions[0].slice(lineBuffer.length);
          lineBuffer += completion;
          term.write(completion);
        }
      } else if (printable) {
        lineBuffer += key;
        term.write(key);
      }
    });

    const handleResize = () => {
      try { fitAddon.fit(); } catch(e) {}
    };
    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
      term.dispose();
    };
  }, [device, hostname]);

  const QUICK_CMDS = [
    'show version', 'show ip interface brief', 'show arp',
    'show ip route', 'show interfaces', 'show log', 'show ip access-lists'
  ];

  const runQuickCmd = (cmd) => {
    if (!xtermRef.current) return;
    const term = xtermRef.current;
    term.writeln(`\x1b[32m${promptRef.current} \x1b[33m${cmd}\x1b[0m`);
    const parts = cmd.split(/\s+/);
    const handler = CISCO_RESPONSES[cmd] || CISCO_RESPONSES[parts[0]];
    if (handler) {
      term.writeln(`\x1b[37m${handler(device, parts)}\x1b[0m`);
    }
    term.write(`\x1b[32m${promptRef.current} \x1b[0m`);
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center p-4" data-testid="cisco-terminal">
      <div className="w-full max-w-5xl h-[80vh] flex flex-col rounded-sm border border-green-500/50 shadow-2xl shadow-green-500/20">
        {/* Terminal Header */}
        <div className="flex items-center justify-between px-4 py-2 bg-[#0a1a0a] border-b border-green-500/30">
          <div className="flex items-center gap-3">
            <Terminal className="h-4 w-4 text-green-400" />
            <span className="text-green-400 font-mono text-sm font-bold">{hostname}</span>
            <Badge className="bg-green-500/20 text-green-400 border-green-500/30 border text-xs font-mono">
              {(device.ip_addresses || [])[0] || 'N/A'}
            </Badge>
            <Badge variant="outline" className="text-xs font-mono">{device.type?.toUpperCase()}</Badge>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-green-400/50 text-xs font-mono">Cisco IOS 15.7</span>
            <Button size="icon" variant="ghost" className="h-7 w-7 text-green-400 hover:text-red-400" onClick={onClose}>
              <X className="h-4 w-4" />
            </Button>
          </div>
        </div>

        {/* Quick Commands */}
        <div className="flex gap-2 px-4 py-2 bg-[#050f05] border-b border-green-500/20 flex-wrap">
          <span className="text-green-600 text-xs font-mono self-center">QUICK:</span>
          {QUICK_CMDS.map(cmd => (
            <button
              key={cmd}
              onClick={() => runQuickCmd(cmd)}
              className="text-xs font-mono text-green-400 hover:text-green-200 bg-green-500/10 hover:bg-green-500/20 px-2 py-0.5 rounded border border-green-500/20 transition-colors"
              data-testid={`quick-cmd-${cmd.replace(/\s+/g, '-')}`}
            >
              {cmd}
            </button>
          ))}
        </div>

        {/* xterm */}
        <div ref={termRef} className="flex-1 overflow-hidden bg-[#0a0a0a] p-2" />
      </div>
    </div>
  );
}

export default CiscoTerminal;
